import React, { useCallback, useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import useUiuxStore from "src/store/useUiuxStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { InsertRowHandler } from "src/utils/grid/GridHandlerContent";
import { ShowSuccessFetch, showSuccess, showWarning } from "src/utils/toast/toastUtil";
import {
    downloadExcel_HRM3050_R01, getHRM3050_R01AllCount, getHRM3050_R01DetailList, getHRM3050_R01List,
    patchHRM3050_R01, postHRM3050_R01, postHRM3050_R01AttendDailyRecalculation,
} from "./Api_HRM3050_R01";
import {
    defaultHRM3050_R01DetailDto, defaultHRM3050_R01HeaderDto, HRM3050_R01DetailDto, HRM3050_R01Dto, HRM3050_R01HeaderDto,
} from "./Dto_HRM3050_R01";
import { HRM3050_R01Filter, useHRM3050_R01Store } from "./Store_HRM3050_R01";
import { findHRM3050_R01BreakOverlap, HRM3050_R01GridType, validateHRM3050_R01Time } from "./Util_HRM3050_R01";

/** Container에서 전달받는 값의 모양이다. */
interface Props {
    gridRef: React.RefObject<AgGridReact<HRM3050_R01HeaderDto> | null>; // 근무조 그리드 ref. 편집 확정과 조회 후 스크롤 이동에 쓴다.
    detailGridRef: React.RefObject<AgGridReact<HRM3050_R01DetailDto> | null>; // 휴게 그리드 ref. 편집 확정에 쓴다.
    setMessage: (message: string) => void; // 조회·저장 결과를 하단 메시지로 표시하는 함수
}
/** 조회 함수의 부가 옵션이다. */
type FetchOptions = {
    messageMode?: 'show' | 'none'; // none이면 조회 완료·빈 목록 알림을 표시하지 않는다.
    shiftCd?: string; // 조회 후 선택할 근무조코드. 없으면 직전 선택 근무조, 그것도 없으면 첫 행을 선택한다.
};

/**
 * 화면의 조회, 근무조 선택 전환, 등록, 휴게 삭제 표시, 근무조 1건 단위 저장, 휴게 Excel, 일일근태 재계산 동작을 구성한다.
 * @param gridRef 근무조 그리드 참조
 * @param detailGridRef 휴게 그리드 참조
 * @param setMessage 조회·저장 결과를 표시하는 화면 메시지 변경 함수
 * @returns Container와 Table이 버튼·그리드에 연결할 상태 및 동작 함수
 */
export const HRM3050_R01Content = ({ gridRef, detailGridRef, setMessage }: Props) => {
    // 화면 Store에서 검색 조건, 근무조·휴게 목록과 선택 상태, 변경 함수를 가져온다.
    const {
        setHRM3050_R01PastFilter,
        HRM3050_R01List, setHRM3050_R01List, insertHRM3050_R01List, setOriginalHRM3050_R01List,
        selectedHRM3050_R01, setSelectedHRM3050_R01,
        HRM3050_R01DetailList, setHRM3050_R01DetailList, insertHRM3050_R01DetailList, setOriginalHRM3050_R01DetailList,
        setAllCount, setScrollTop, setDetailScrollTop, initData,
    } = useHRM3050_R01Store();
    // 공통 확인창(ConfirmModal)의 내용과 표시 여부를 바꾸는 함수를 가져온다.
    const { setConfirmModalProps, setConfirmModalVisible } = useUiuxStore();
    const { setLoadingSpinner } = useLoadingSpinnerStore();
    // true이면 휴게 행 클릭을 선택으로 처리한다. +/- 버튼 직후에는 false가 되어 같은 클릭을 한 번 무시한다.
    const [clickable, setClickable] = useState(true);

    useFetchActionbarAuth("HRM3050_R01");

    /** 빈 검색값을 undefined로 바꿔 요청에서 제외할 조건 객체를 반환한다. */
    const getParams = useCallback((filter: HRM3050_R01Filter): Partial<HRM3050_R01Filter> => ({
        // ||: 빈 문자열이면 undefined로 바꿔 쿼리 파라미터에서 빠지게 한다.
        useYn: filter.useYn || undefined,
    }), []);

    /**
     * 근무조의 휴게 목록을 서버에서 읽어 휴게 그리드 상태를 새로 채운다. 선택·삭제 표시와 스크롤도 초기화한다.
     * @param header 휴게를 읽을 근무조. 없거나 아직 저장하지 않은 신규 근무조이면 빈 목록으로 둔다.
     * @returns 조회 완료를 나타내는 Promise<void>. 오류는 호출자에게 전파한다.
     */
    const loadHRM3050_R01DetailList = useCallback(async (header?: HRM3050_R01HeaderDto) => {
        const state = useHRM3050_R01Store.getState();
        state.initSelectedHRM3050_R01DetailList();
        state.removeDeletedHRM3050_R01DetailList();
        setDetailScrollTop(null);
        // 신규 근무조는 서버에 휴게가 없으므로 요청하지 않는다.
        if (!header || header.inserted) {
            setHRM3050_R01DetailList([]);
            setOriginalHRM3050_R01DetailList([]);
            return;
        }
        const response = await getHRM3050_R01DetailList(header.shiftCd);
        // 공통 인터셉터는 실패 응답도 오류 알림 후 그대로 넘겨주므로, 실패하면 휴게 상태를 바꾸지 않는다.
        if (!response.data.successFlag) return;
        setHRM3050_R01DetailList(response.data.data);
        setOriginalHRM3050_R01DetailList(response.data.data);
    }, [setDetailScrollTop, setHRM3050_R01DetailList, setOriginalHRM3050_R01DetailList]);

    /**
     * 근무조 목록과 전체 건수를 같은 조건으로 조회하고, 근무조 한 행을 선택해 그 휴게를 불러온다.
     * 검색 조건은 초기화 직후에도 최신 값을 쓰도록 렌더 시점 값 대신 Store에서 직접 읽는다.
     * @param fetchType search는 스크롤을 초기화하고 refetch는 저장 뒤 목록을 다시 읽는다.
     * @param options 알림 표시 여부와 조회 후 선택할 근무조코드
     * @returns 조회 완료를 나타내는 Promise<void>. 오류는 호출자에게 전파한다.
     */
    const executeDataFetch = useCallback(async (fetchType: 'search' | 'refetch', options?: FetchOptions) => {
        setLoadingSpinner(true);
        try {
            const filter = useHRM3050_R01Store.getState().HRM3050_R01Filter;
            // ?.: 선택 근무조가 없으면 undefined가 된다. 조회 전에 기억해 두었다가 같은 근무조를 다시 선택한다.
            const previousShiftCd = useHRM3050_R01Store.getState().selectedHRM3050_R01?.shiftCd;
            // 사용자가 새 조회를 요청한 경우에만 이전 스크롤 위치를 버린다.
            if (fetchType === 'search') setScrollTop(null);
            const response = await getHRM3050_R01List(fetchType, getParams(filter), 0);
            // 실패 응답은 공통 인터셉터가 이미 알렸으므로 기존 목록을 유지하고 끝낸다.
            if (!response.data.successFlag) return;
            const list = response.data.data;
            setHRM3050_R01List(list);
            setOriginalHRM3050_R01List(list);
            // valueIndex가 채워진 Store 목록에서 선택할 행을 찾는다.
            const gridList = useHRM3050_R01Store.getState().HRM3050_R01List;
            // ??: 지정 코드 → 직전 선택 코드 → 첫 행 순서로 선택할 근무조를 정한다. 실행 샘플도 첫 근무조를 선택한 상태로 연다.
            const targetShiftCd = options?.shiftCd ?? previousShiftCd;
            const targetRow = gridList.find(row => row.shiftCd === targetShiftCd) ?? gridList[0];
            setSelectedHRM3050_R01(targetRow);
            await loadHRM3050_R01DetailList(targetRow);
            const countResponse = await getHRM3050_R01AllCount(getParams(filter));
            // 삼항 연산자: 건수 조회가 실패하면 data가 숫자가 아니므로 받은 목록 건수로 대신한다.
            const count = countResponse.data.successFlag ? countResponse.data.data : list.length;
            setAllCount(count);
            // 목록이 있으면 요청한 경우에만 완료 메시지를 표시한다.
            if (list.length > 0) {
                if (options?.messageMode !== 'none') {
                    ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${list.length}/${count}건)`);
                }
            // 결과가 없고 메시지를 허용한 경우에는 빈 목록을 알린다.
            } else if (options?.messageMode !== 'none') {
                showWarning('데이터가 없습니다.');
            }
            setHRM3050_R01PastFilter(filter);
        } finally {
            // 조회 성공·실패와 관계없이 로딩 표시를 해제한다.
            setLoadingSpinner(false);
        }
    }, [setLoadingSpinner, setScrollTop, getParams, setHRM3050_R01List, setOriginalHRM3050_R01List,
        setSelectedHRM3050_R01, loadHRM3050_R01DetailList, setAllCount, setMessage, setHRM3050_R01PastFilter]);

    /** 조회 버튼에서 일반 조회를 실행하고 조회 Promise를 반환한다. */
    const fetchData = useCallback(() => executeDataFetch('search'), [executeDataFetch]);

    /**
     * 선택 근무조나 휴게에 저장하지 않은 변경이 있는지 확인한다.
     * 근무조는 신규·수정 행, 휴게는 내용이 있는 신규 행·수정 행·삭제 표시를 변경으로 본다.
     * @returns 변경이 있으면 true
     */
    const hasHRM3050_R01Changes = useCallback((): boolean => {
        const state = useHRM3050_R01Store.getState();
        // 신규 근무조는 코드나 이름을 입력했을 때만 변경으로 본다. 빈 목록에 자동으로 넣은 빈 행 때문에 확인창이 뜨지 않게 한다.
        const isHeaderChanged = state.HRM3050_R01List.some(row => row.isModified
            || (row.inserted && (!!row.shiftCd.trim() || !!row.shiftNm.trim())));
        const isDetailChanged = state.deletedHRM3050_R01DetailList.length > 0
            || state.HRM3050_R01DetailList.some(row => row.isModified
                || (row.inserted && (!!row.breakNm.trim() || !!row.fromTime || !!row.toTime)));
        return isHeaderChanged || isDetailChanged;
    }, []);

    /**
     * 저장하지 않은 변경을 버린다. 신규 근무조 행은 빼고, 수정한 근무조는 조회 원본 값으로 되돌린다.
     * 휴게는 이어서 다른 근무조의 휴게를 새로 불러오므로 여기서 따로 되돌리지 않는다.
     * @returns 반환값 없이 근무조 목록을 갱신한다.
     */
    const discardHRM3050_R01Changes = useCallback(() => {
        const state = useHRM3050_R01Store.getState();
        const restoredList = state.HRM3050_R01List
            .filter(row => !row.inserted)
            // ??: 원본을 찾지 못하면 현재 행을 그대로 둔다.
            .map(row => state.originalHRM3050_R01List.find(original => original.valueIndex === row.valueIndex) ?? row);
        setHRM3050_R01List(restoredList);
    }, [setHRM3050_R01List]);

    /**
     * 저장하지 않은 변경이 있으면 공통 확인창으로 버릴지 묻고, 확인하면 변경을 버린 뒤 다음 동작을 실행한다.
     * 확인창 버튼은 확인(버리고 진행)·취소(그대로 머무름) 두 개뿐이며 "저장하고 이동"은 두지 않는다.
     * @param onConfirm 변경이 없거나 확인을 누른 뒤 실행할 동작
     * @returns 반환값은 없다.
     */
    const confirmHRM3050_R01Discard = useCallback((onConfirm: () => void) => {
        // 버릴 변경이 없으면 묻지 않고 바로 진행한다.
        if (!hasHRM3050_R01Changes()) {
            onConfirm();
            return;
        }
        /** 확인창 상태와 props를 함께 정리해 다음 확인창에 값이 남지 않게 한다. (MST4060_R01과 동일) */
        const closeConfirm = () => {
            setConfirmModalVisible(false);
            setConfirmModalProps(undefined);
        };
        setConfirmModalProps({
            title: '변경 내용 확인',
            message: '저장하지 않은 변경 내용이 있습니다.\n변경 내용을 버리고 진행하시겠습니까?',
            confirmText: '확인',
            cancelText: '취소',
            onConfirm: () => {
                closeConfirm();
                discardHRM3050_R01Changes();
                onConfirm();
            },
            onCancel: closeConfirm,
        });
        setConfirmModalVisible(true);
    }, [hasHRM3050_R01Changes, setConfirmModalProps, setConfirmModalVisible, discardHRM3050_R01Changes]);

    /**
     * 근무조 행 클릭으로 선택 근무조를 바꾼다. 같은 행이면 아무것도 하지 않고, 변경이 있으면 확인창을 먼저 띄운다.
     * @param row 클릭한 근무조 행
     * @returns 반환값은 없다.
     */
    const handleHeaderRowClick = useCallback((row: HRM3050_R01HeaderDto) => {
        // 이미 선택한 근무조를 다시 누르면 선택을 유지한다.
        if (row.valueIndex === useHRM3050_R01Store.getState().selectedHRM3050_R01?.valueIndex) return;
        confirmHRM3050_R01Discard(() => {
            // 변경을 버리면서 목록이 바뀌었을 수 있으므로 Store에서 같은 행을 다시 찾는다.
            const targetRow = useHRM3050_R01Store.getState().HRM3050_R01List
                .find(item => item.valueIndex === row.valueIndex);
            setSelectedHRM3050_R01(targetRow);
            void loadHRM3050_R01DetailList(targetRow);
        });
    }, [confirmHRM3050_R01Discard, setSelectedHRM3050_R01, loadHRM3050_R01DetailList]);

    /** 신규 근무조 행 기본값을 반환한다. */
    const defaultHRM3050_R01HeaderRow = useCallback((): HRM3050_R01HeaderDto => ({
        ...defaultHRM3050_R01HeaderDto,
        inserted: true,
    }), []);

    /** 선택 근무조 코드를 가진 신규 휴게 행 기본값을 반환한다. 순번은 저장할 때 서버가 붙인다. */
    const defaultHRM3050_R01DetailRow = useCallback((): HRM3050_R01DetailDto => ({
        ...defaultHRM3050_R01DetailDto,
        // ??: 선택 근무조가 없으면 빈 코드로 둔다. 이 경우 행 추가 자체를 막는다.
        shiftCd: selectedHRM3050_R01?.shiftCd ?? '',
        inserted: true,
    }), [selectedHRM3050_R01]);

    /** 화면 Store와 안내 메시지를 초기화한다. 다른 HRM 화면과 같이 조회하지 않고 빈 화면으로 둔다. */
    const initContainerData = useCallback(() => {
        initData();
        setMessage('');
    }, [initData, setMessage]);

    /**
     * 등록 버튼 또는 휴게 행의 + 버튼으로 신규 행을 만든다.
     * 근무조 신규 행은 저장 단위가 바뀌므로 변경 확인 후 맨 위에 추가하고 바로 선택한다. 휴게는 선택 근무조가 있어야 추가한다.
     * @param event 버튼 클릭 이벤트
     * @param rowIndex 값이 있으면 해당 휴게 행을 기준으로 삽입한다.
     * @param gridType 행을 추가할 그리드. 없으면(등록 버튼) 마지막으로 클릭한 그리드에 추가한다.
     * @returns 반환값 없이 Store 목록을 갱신한다.
     */
    const handleInsertClick = useCallback((event: React.MouseEvent, rowIndex?: number, gridType?: HRM3050_R01GridType) => {
        // ??: + 버튼은 자기 그리드를, 등록 버튼은 마지막으로 클릭한 그리드를 대상으로 한다.
        // 대상 그리드는 렌더 시점 값이 아니라 Store에서 바로 읽는다. activeGridType을 의존성에 넣으면
        // 휴게 영역을 누르는 순간(mousedown) 이 함수가 새로 만들어지고, 이를 쓰는 휴게 그리드 컬럼 정의가 다시 만들어져
        // + 버튼이 mouseup 전에 다시 그려진다. 그러면 click 이벤트가 사라져 + 버튼이 첫 클릭에 동작하지 않는다.
        const targetGridType = gridType ?? useHRM3050_R01Store.getState().activeGridType;
        if (targetGridType === 'header') {
            confirmHRM3050_R01Discard(() => {
                const defaultRow = defaultHRM3050_R01HeaderRow();
                // valueIndex -1은 기준 행이 없다는 뜻이라 공통 삽입 처리가 맨 위에 넣는다.
                InsertRowHandler({
                    event,
                    dataList: useHRM3050_R01Store.getState().HRM3050_R01List,
                    rowData: { ...defaultRow, valueIndex: -1 },
                    insertRow: insertHRM3050_R01List,
                    defaultRowData: defaultRow,
                });
                // 새 행은 가장 큰 valueIndex를 받으므로 그 행을 선택하고 휴게를 비운다.
                const newRow = useHRM3050_R01Store.getState().HRM3050_R01List
                    .reduce<HRM3050_R01HeaderDto | undefined>((max, row) => !max || row.valueIndex > max.valueIndex ? row : max, undefined);
                setSelectedHRM3050_R01(newRow);
                void loadHRM3050_R01DetailList(newRow);
                // ?.: 화면을 연 직후 빈 행을 넣을 때는 그리드가 아직 준비되지 않아 api가 없으므로 스크롤 이동을 건너뛴다.
                gridRef.current?.api?.ensureIndexVisible(0, 'top');
            });
            return;
        }
        // 휴게는 근무조에 속하므로 선택 근무조 없이 추가할 수 없다. 메시지는 실행 샘플과 같다.
        if (!selectedHRM3050_R01) {
            showWarning('근무조를 먼저 선택하세요.');
            return;
        }
        // 행 번호가 전달되면 행의 + 버튼 동작이므로 해당 행을 삽입 기준으로 사용한다.
        const isGridInsert = rowIndex !== undefined;
        const defaultRow = defaultHRM3050_R01DetailRow();
        InsertRowHandler({
            event,
            dataList: HRM3050_R01DetailList,
            rowData: isGridInsert ? HRM3050_R01DetailList[rowIndex] : { ...defaultRow, valueIndex: -1 },
            insertRow: insertHRM3050_R01DetailList,
            defaultRowData: defaultRow,
            // 펼치기(...): + 버튼일 때만 setClickable 옵션을 넣어 같은 클릭이 행 선택으로 처리되지 않게 한다.
            ...(isGridInsert && { setClickable }),
        });
    }, [confirmHRM3050_R01Discard, defaultHRM3050_R01HeaderRow, insertHRM3050_R01List,
        setSelectedHRM3050_R01, loadHRM3050_R01DetailList, gridRef, selectedHRM3050_R01,
        defaultHRM3050_R01DetailRow, HRM3050_R01DetailList, insertHRM3050_R01DetailList]);

    /**
     * 저장할 근무조의 필수값, 코드 중복, 시각 형식을 검사한다. 메시지는 백엔드 검증과 같은 문장을 쓴다.
     * @param header 저장할 근무조
     * @param headerList 코드 중복 비교에 쓸 근무조 목록
     * @returns 모두 유효하면 true, 첫 실패 항목을 경고하고 저장을 막으면 false
     */
    const validateHRM3050_R01Header = useCallback((header: HRM3050_R01HeaderDto, headerList: HRM3050_R01HeaderDto[]): boolean => {
        const code = header.shiftCd.trim().toUpperCase();
        // 공백 제거 뒤 코드가 없으면 필수값 오류를 알리고 검증을 중단한다.
        if (!code) { showWarning('근무조코드를 입력해주세요.'); return false; }
        // 신규 근무조는 화면 목록의 다른 코드와 같으면 저장할 수 없다. 앞뒤 공백과 대소문자는 무시한다.
        if (header.inserted && headerList.some(other => other.valueIndex !== header.valueIndex
            && other.shiftCd.trim().toUpperCase() === code)) {
            showWarning('이미 등록된 근무조입니다. > ' + code); return false;
        }
        // 근무조명이 빈 문자열 또는 공백뿐이면 저장할 수 없다.
        if (!header.shiftNm.trim()) { showWarning('근무조명을 입력해주세요. > ' + code); return false; }
        // 정시 출근·퇴근은 지각·연장 계산 기준이라 HH:MM 형식이어야 한다.
        if (!validateHRM3050_R01Time(header.startTime)) {
            showWarning('정시 출근은 HH:MM 형식(00:00~23:59)이어야 합니다. > ' + code); return false;
        }
        if (!validateHRM3050_R01Time(header.endTime)) {
            showWarning('정시 퇴근은 HH:MM 형식(00:00~23:59)이어야 합니다. > ' + code); return false;
        }
        // 출근과 퇴근이 같으면 근무 시간이 0인지 24시간인지 정할 수 없다.
        if (header.startTime === header.endTime) {
            showWarning('정시 출근과 퇴근 시각이 같습니다. > ' + code); return false;
        }
        return true;
    }, []);

    /**
     * 저장할 휴게 행의 필수값, 시각 형식, 시작·종료 동일 여부, 사용 휴게끼리의 겹침을 검사한다.
     * 메시지는 실행 샘플·백엔드와 같은 문장을 쓴다.
     * @param detailList 삭제 표시와 빈 신규 행을 뺀 휴게 행 목록
     * @returns 모두 유효하면 true, 첫 실패 항목을 경고하고 저장을 막으면 false
     */
    const validateHRM3050_R01DetailList = useCallback((detailList: HRM3050_R01DetailDto[]): boolean => {
        for (const row of detailList) {
            // 휴게명이 빈 문자열 또는 공백뿐이면 저장할 수 없다.
            if (!row.breakNm.trim()) { showWarning('휴게명을 입력해주세요.'); return false; }
            // 시작·종료 중 하나라도 HH:MM 형식이 아니면 시간 계산을 할 수 없다.
            if (!validateHRM3050_R01Time(row.fromTime) || !validateHRM3050_R01Time(row.toTime)) {
                showWarning('휴게 시작·종료는 HH:MM 형식(00:00~23:59)이어야 합니다. > ' + row.breakNm); return false;
            }
            // 시작과 종료가 같으면 휴게 시간이 0분인지 24시간인지 정할 수 없다.
            if (row.fromTime === row.toTime) { showWarning(`${row.breakNm}: 시작과 종료 시각이 같습니다.`); return false; }
        }
        const overlap = findHRM3050_R01BreakOverlap(detailList);
        // 사용 중인 휴게끼리 시간이 겹치면 계산 시 휴게가 두 번 빠지므로 저장을 막는다.
        if (overlap) {
            // 구조 분해: 겹치는 두 휴게 행을 꺼낸다.
            const [first, second] = overlap;
            showWarning(`휴게시간이 겹칩니다: ${first.breakNm}(${first.fromTime}~${first.toTime}) · ${second.breakNm}(${second.fromTime}~${second.toTime})`);
            return false;
        }
        return true;
    }, []);

    /**
     * 선택 근무조 1건과 그 휴게 목록을 한 번에 저장한다. 신규 근무조는 POST, 기존 근무조는 PATCH로 보낸다.
     * 휴게는 삭제 표시한 행을 뺀 전체 목록을 보내고, 서버가 DB와 비교해 등록·수정·삭제한다.
     * @returns 저장과 저장 후 재조회가 완료되는 Promise<void>. 요청 오류는 전파한다.
     */
    const handleSaveClick = useCallback(async () => {
        gridRef.current?.api.stopEditing();
        detailGridRef.current?.api.stopEditing();
        // stopEditing이 발생시킨 최신 셀 값을 읽기 위해 렌더 시점의 목록 대신 Store를 다시 조회한다.
        const state = useHRM3050_R01Store.getState();
        // 저장 단위가 근무조 1건이므로 선택 근무조가 없으면 저장할 수 없다.
        if (!state.selectedHRM3050_R01) {
            showWarning('근무조를 먼저 선택하세요.');
            return;
        }
        // ??: 목록에서 최신 행을 찾지 못하면 선택 값을 그대로 쓴다.
        const header = state.HRM3050_R01List.find(row => row.valueIndex === state.selectedHRM3050_R01?.valueIndex)
            ?? state.selectedHRM3050_R01;
        // 삭제 목록에는 숫자(행 클릭 후 삭제)와 문자열(전체 선택 후 삭제)이 섞일 수 있어 문자열로 맞춰 비교한다.
        const deletedSet = new Set(state.deletedHRM3050_R01DetailList.map(String));
        /** valueIndex 문자열이 삭제 표시 목록에 있으면 true를 반환한다. */
        const isDeleted = (row: HRM3050_R01DetailDto) => deletedSet.has(String(row.valueIndex));
        /** 휴게명·시작·종료가 모두 비어 있는 신규 그리드 행이면 true를 반환한다. */
        const isBlank = (row: HRM3050_R01DetailDto) =>
            row.inserted && !row.breakNm.trim() && !row.fromTime && !row.toTime;
        const activeDetailList = state.HRM3050_R01DetailList.filter(row => !isDeleted(row) && !isBlank(row));
        const isDetailChanged = activeDetailList.some(row => row.inserted || row.isModified)
            || state.HRM3050_R01DetailList.some(row => isDeleted(row) && !row.inserted);

        // 근무조와 휴게 모두 바뀐 것이 없으면 서버 요청을 보내지 않는다.
        if (!header.inserted && !header.isModified && !isDetailChanged) {
            showWarning('저장할 내용이 없습니다.');
            return;
        }
        // 근무조나 휴게 중 하나라도 검증에 실패하면 요청하지 않는다.
        if (!validateHRM3050_R01Header(header, state.HRM3050_R01List)) return;
        if (!validateHRM3050_R01DetailList(activeDetailList)) return;

        const shiftCd = header.shiftCd.trim().toUpperCase();
        const saveDto: HRM3050_R01Dto = {
            hrmWorkShiftDto: { ...header, shiftCd },
            // 신규 휴게는 순번을 null로 보내 서버가 기존 최대 순번 다음 번호를 붙이게 한다.
            // 삼항 연산자: 신규 행이면 null, 기존 행이면 조회한 순번을 그대로 보낸다.
            hrmBreakTimeDtoList: activeDetailList.map(row => ({ ...row, shiftCd, breakSeq: row.inserted ? null : row.breakSeq })),
        };
        setLoadingSpinner(true);
        try {
            // 삼항 연산자: 신규 근무조는 등록(POST), 기존 근무조는 수정(PATCH) 요청을 보낸다.
            const response = header.inserted ? await postHRM3050_R01(saveDto) : await patchHRM3050_R01(saveDto);
            // 서버 검증 실패는 공통 인터셉터가 서버 안내 문장으로 알리므로, 완료 알림 없이 편집 상태를 그대로 둔다.
            if (!response.data.successFlag) return;
            showSuccess('근무시간·휴게 기준 저장이 완료되었습니다.');
            setMessage('근무시간·휴게 기준 저장이 완료되었습니다.');
            // 저장한 근무조를 다시 선택한 상태로 목록과 휴게를 재조회한다.
            await executeDataFetch('refetch', { messageMode: 'none', shiftCd: response.data.data.hrmWorkShiftDto.shiftCd });
        } finally {
            setLoadingSpinner(false);
        }
    }, [gridRef, detailGridRef, validateHRM3050_R01Header, validateHRM3050_R01DetailList,
        setLoadingSpinner, setMessage, executeDataFetch]);

    /**
     * 삭제 버튼의 표시·복구 동작을 휴게 그리드에 처리한다. 근무조는 다른 화면이 코드로 참조하므로 삭제하지 않는다.
     * @returns 반환값 없이 선택 및 삭제 표시 상태를 갱신한다.
     */
    const handleDeleteClick = useCallback(() => {
        const state = useHRM3050_R01Store.getState();
        // 근무조 그리드가 대상이면 삭제 대신 사용 N을 안내한다.
        if (state.activeGridType === 'header') {
            showWarning('근무조는 삭제할 수 없습니다. 사용하지 않으려면 사용을 N으로 바꾸세요.');
            return;
        }
        // 삭제 표시가 이미 있으면 버튼 동작을 휴게 그리드 전체 복구로 처리한다. (HRM3110_R01과 동일)
        if (state.deletedHRM3050_R01DetailList.length > 0) {
            state.removeDeletedHRM3050_R01DetailList();
            state.removeSelectedHRM3050_R01DetailList();
            return;
        }
        // 선택된 행이 없으면 삭제할 대상이 없으므로 종료한다.
        if (state.selectedHRM3050_R01DetailList.length === 0) return;
        // 선택 목록에는 숫자(행 클릭)와 문자열(전체 선택)이 섞일 수 있어 문자열로 맞춰 비교한다.
        const selectedSet = new Set(state.selectedHRM3050_R01DetailList.map(String));
        const gridList = state.HRM3050_R01DetailList;
        // 신규 행은 서버에 없으므로 목록에서 바로 지운다.
        gridList.filter(row => selectedSet.has(String(row.valueIndex)) && row.inserted)
            .forEach(row => state.removeHRM3050_R01DetailList(row.valueIndex));
        // 선택 목록의 값을 형 변환 없이 그대로 삭제 목록으로 옮긴다.
        // 행 클릭 Hook과 행 번호 D 표시가 숫자 그대로 비교하므로, 같은 형을 유지해야 삭제 행 선택 차단과 D 표시가 맞는다. (HRM3110_R01과 동일)
        const existingIds = state.selectedHRM3050_R01DetailList.filter(id =>
            gridList.some(row => String(row.valueIndex) === String(id) && !row.inserted));
        // 서버에 이미 존재하는 행만 저장 시 삭제할 목록에 표시한다.
        if (existingIds.length > 0) state.addDeletedHRM3050_R01DetailList(existingIds);
        state.removeSelectedHRM3050_R01DetailList();
    }, []);

    /**
     * 선택 근무조의 휴게 목록 Excel 파일을 내려받는다. 저장된 근무조만 서버에 휴게가 있으므로 신규 근무조는 막는다.
     * @returns 다운로드 응답 처리가 끝나는 Promise<void>. 오류는 호출자에게 전파한다.
     */
    const handleExcelClick = useCallback(async () => {
        // 저장 전 신규 근무조는 서버에 데이터가 없어 파일을 만들 수 없다.
        if (!selectedHRM3050_R01 || selectedHRM3050_R01.inserted) {
            showWarning('저장된 근무조를 먼저 선택하세요.');
            return;
        }
        setLoadingSpinner(true);
        try {
            const response = await downloadExcel_HRM3050_R01(selectedHRM3050_R01.shiftCd);
            // 실패 응답은 파일 데이터가 없으므로 다운로드하지 않는다. 알림은 공통 인터셉터가 한 번 띄운다.
            if (!response.data.successFlag) return;
            downloadSettingExcel({
                responseData: response.data.data,
                fileName: `근무시간·휴게 기준_${selectedHRM3050_R01.shiftCd}-excel.xlsx`,
            });
        } finally {
            setLoadingSpinner(false);
        }
    }, [selectedHRM3050_R01, setLoadingSpinner]);

    /**
     * 일일근태 재계산을 요청한다. 바뀐 기준을 저장하지 않았으면 이전 기준으로 계산되므로 먼저 저장하게 한다.
     * 대상 일일근태가 없으면 0건으로 끝난다. 대상이 있는데 계산 기능(HRM2010_R01)이 아직 없으면 서버가 안내 오류를 돌려준다.
     * @returns 요청 처리가 끝나는 Promise<void>. 요청 오류는 공통 오류 처리로 표시된다.
     */
    const handleRecalculateClick = useCallback(async () => {
        // 메시지는 실행 샘플과 같다.
        if (hasHRM3050_R01Changes()) {
            showWarning('변경한 기준을 먼저 저장한 뒤 재계산하세요.');
            return;
        }
        setLoadingSpinner(true);
        try {
            const response = await postHRM3050_R01AttendDailyRecalculation();
            // 대상이 있는데 계산 기능이 아직 없는 경우 등 실패 응답은 공통 인터셉터가 서버 안내 문장으로 한 번만 알린다.
            // 여기서 data(안내 문장)를 건수로 읽어 완료 알림을 또 띄우지 않도록 바로 끝낸다.
            if (!response.data.successFlag) return;
            const count = response.data.data;
            // 재계산할 일일근태가 없으면 바뀐 것이 없음을 알린다.
            if (count === 0) {
                showWarning('재계산할 일일근태가 없습니다.');
                return;
            }
            showSuccess(`일일근태 ${count.toLocaleString()}건을 다시 계산했습니다.`);
            setMessage(`일일근태 ${count.toLocaleString()}건을 다시 계산했습니다.`);
        } finally {
            setLoadingSpinner(false);
        }
    }, [hasHRM3050_R01Changes, setLoadingSpinner, setMessage]);

    // 화면을 열 때 자동 조회하지 않고, 조회 버튼을 눌러야 조회한다. 근무조 목록이 비어 있으면(최초 진입·초기화·0건 조회)
    // 바로 입력할 수 있도록 빈 신규 근무조 행을 넣고 선택한다. (PRD2010_R01 표준 패턴과 동일)
    // 의존성: 근무조 목록이 바뀔 때마다 비었는지 다시 확인한다.
    useEffect(() => {
        if (HRM3050_R01List.length === 0) {
            handleInsertClick({} as React.MouseEvent, undefined, 'header');
        }
    }, [HRM3050_R01List, handleInsertClick]);
    // 선택 근무조의 휴게 목록이 비어 있으면 같은 이유로 빈 신규 휴게 행을 하나 넣는다.
    // 휴게는 근무조에 속하므로 선택 근무조가 있을 때만 넣는다.
    // 의존성: 선택 근무조나 휴게 목록이 바뀔 때마다 다시 확인한다.
    useEffect(() => {
        if (selectedHRM3050_R01 && HRM3050_R01DetailList.length === 0) {
            handleInsertClick({} as React.MouseEvent, undefined, 'detail');
            // 등록 처리 중 행 클릭이 막히지 않도록 클릭 가능 상태로 되돌린다.
            setClickable(true);
        }
    }, [selectedHRM3050_R01, HRM3050_R01DetailList, handleInsertClick]);

    // Container·Table이 필요한 함수만 노출한다. 데이터 값은 Store에서 읽는다.
    return {
        clickable, setClickable, executeDataFetch, fetchData,
        defaultHRM3050_R01HeaderRow, defaultHRM3050_R01DetailRow, initContainerData,
        handleHeaderRowClick, handleInsertClick, handleSaveClick, handleDeleteClick,
        handleExcelClick, handleRecalculateClick,
    };
};
